import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AlertTriangle, IndianRupee, TrendingUp, WalletCards } from "lucide-react";
import DataTable from "../components/common/DataTable";
import StatCard from "../components/common/StatCard";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { erpApi, formatCurrency, formatDate } from "../services/erpApi";

const COLORS = ["#0F4C3A", "#2D6A4F", "#52B788", "#F4A261", "#E76F51", "#457B9D", "#7C3AED", "#64748B"];

const moneyTooltip = (value) => formatCurrency(value);

export default function Analytics() {
  const [data, setData] = useState({
    totals: {},
    revenue_trends: [],
    expense_breakdown: [],
    expense_monthly: [],
    finance_monthly: [],
    expense_details: [],
    sample_status: [],
  });

  useEffect(() => {
    erpApi.analytics().then((res) => setData(res.data)).catch(() => toast.error("Failed to load analytics"));
  }, []);

  const totals = data.totals || {};
  const categoryRows = (data.expense_breakdown || []).map((row) => ({
    ...row,
    label: row.category?.replaceAll("_", " "),
  }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-manrope text-2xl font-bold">Analytics</h1>
        <p className="text-sm text-hospital-text-muted">Hospital expense report, revenue, profit, samples and attendance insights.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Total Revenue" value={formatCurrency(totals.total_revenue)} icon={IndianRupee} helper="All non-draft bills" to="/billing" />
        <StatCard title="Hospital Expenses" value={formatCurrency(totals.total_expenses)} icon={WalletCards} tone="warning" helper="Rent, salary, lab, equipment" to="/expenses" />
        <StatCard title="Profit Estimate" value={formatCurrency(totals.profit_estimate)} icon={TrendingUp} tone="success" helper="Revenue minus expenses" />
        <StatCard title="Pending Payments" value={formatCurrency(totals.pending_expenses)} icon={AlertTriangle} tone="warning" helper="Unpaid expense dues" to="/expenses" />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card className="border border-gray-200 xl:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Revenue, Expense & Profit Trend</CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer>
              <LineChart data={data.finance_monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `₹${Number(value || 0) / 1000}k`} />
                <Tooltip formatter={moneyTooltip} />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#0F4C3A" strokeWidth={3} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="expenses" stroke="#E76F51" strokeWidth={3} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="profit" stroke="#457B9D" strokeWidth={3} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-base">Monthly Expense Graph</CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer>
              <AreaChart data={data.expense_monthly}>
                <defs>
                  <linearGradient id="expenseFill" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="5%" stopColor="#E76F51" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#E76F51" stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `₹${Number(value || 0) / 1000}k`} />
                <Tooltip formatter={moneyTooltip} />
                <Area type="monotone" dataKey="expenses" stroke="#E76F51" strokeWidth={3} fill="url(#expenseFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-base">Expense Category Share</CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={categoryRows} dataKey="amount" nameKey="label" innerRadius={55} outerRadius={105} paddingAngle={3}>
                  {categoryRows.map((_, index) => <Cell key={index} fill={COLORS[index % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={moneyTooltip} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 xl:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Category Wise Hospital Expense</CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer>
              <BarChart data={categoryRows} layout="vertical" margin={{ left: 20, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tickFormatter={(value) => `₹${Number(value || 0) / 1000}k`} />
                <YAxis dataKey="label" type="category" width={130} />
                <Tooltip formatter={moneyTooltip} />
                <Bar dataKey="amount" radius={[0, 6, 6, 0]}>
                  {categoryRows.map((_, index) => <Cell key={index} fill={COLORS[index % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <DataTable
        title="Expense Details"
        rows={data.expense_details || []}
        empty="No expenses recorded"
        columns={[
          { key: "expense_date", label: "Date", render: (row) => formatDate(row.expense_date) },
          { key: "category", label: "Category", render: (row) => <span className="capitalize">{row.category}</span> },
          { key: "title", label: "Expense" },
          { key: "vendor", label: "Vendor" },
          { key: "payment_mode", label: "Mode", render: (row) => <span className="capitalize">{row.payment_mode}</span> },
          { key: "status", label: "Status", render: (row) => <span className="capitalize">{row.status}</span> },
          { key: "amount", label: "Amount", align: "right", render: (row) => formatCurrency(row.amount) },
        ]}
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-base">Daily Revenue Trend</CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer>
              <BarChart data={data.revenue_trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" />
                <YAxis tickFormatter={(value) => `₹${Number(value || 0) / 1000}k`} />
                <Tooltip formatter={moneyTooltip} />
                <Bar dataKey="revenue" fill="#0F4C3A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-base">Sample Status</CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer>
              <BarChart data={data.sample_status}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="status" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#2D6A4F" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
